import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { toast } from "react-toastify";
import { Modal, Spinner } from "react-bootstrap";
import {
  getRequisitionStockoutItem,
  stockOutRequisitionItem,
} from "../../../services/OtherService";
import { RequisitionStatusBadge } from "../../components/Requisition";
import { getStockByBarcode } from "../../../services/ProductService";
import { searchRequisition } from "../../../services/RackService";
import Select from "react-select";

const EmptyStocks = {
  barcode_no: "",
  stock_id: "",
  net_quantity: 0,
  quantity: 0,
  rack: "",
  isValid: false,
};
const LoadingIndicator = () => {
  return <Spinner color="primary" size={"sm"} delay={0} />;
};

const RequisitionStockOut = forwardRef(
  ({ toggleModal, setIsSubmitLoading }, ref) => {
    const [requisitionNo, setRequisitionNo] = useState(null);
    const [requisitionOption, setRequisitionOption] = useState([]);
    const [isRequisitionNoLoading, setIsRequisitionNoLoading] = useState(false);
    const [requisitionData, setRequisitionData] = useState(null);
    const [requisitionItems, setRequisitionItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [oldestStock, setOldestStock] = useState({
      isModalOpen: false,
      stock: null,
      index: null,
      stockIndex: null,
    });

    useEffect(() => {
      const fetchRequisitionOptions = async () => {
        try {
          setIsRequisitionNoLoading(true);
          const response = await searchRequisition();
          setRequisitionOption(response.data.data || []);
        } catch (error) {
          if (error.response && error.response.data.message) {
            toast.error(error.response.data.message);
          } else {
            toast.error("Failed to fetch requisition options");
          }
        } finally {
          setIsRequisitionNoLoading(false);
        }
      };
      fetchRequisitionOptions();
    }, []);

    const toggleOldestStockModal = () => {
      if (oldestStock.isModalOpen) {
        setOldestStock({ isModalOpen: false, stock: null });
      } else {
        setOldestStock({ isModalOpen: false, ...oldestStock });
      }
    };

    const fetchRequisitionData = async (reqNo) => {
      if (!reqNo) {
        toast.error("Requisition No is required");
        return;
      }
      try {
        setIsLoading(true);
        const response = await getRequisitionStockoutItem(reqNo);
        const { requisition, items } = response.data.data || {};
        setRequisitionData(requisition);
        setRequisitionItems(
          items.map((item) => ({
            ...item,
            approve_quantity: item.requested_quantity - item.fulfilled_quantity,
            stocks: [EmptyStocks],
          })) || []
        );
      } catch (error) {
        if (error.response && error.response.data) {
          toast.error(
            error.response.data.message || "Failed to fetch requisition data"
          );
        } else {
          console.error("Error fetching requisition data:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const handleRequisitionNoChange = (data) => {
      setRequisitionNo(data);
      if (data?.value) {
        fetchRequisitionData(data.value);
      }
      setRequisitionData(null);
      setRequisitionItems([]);
    };

    const handleApproveQuantityChange = (e, index) => {
      let value = parseInt(e.target.value, 10);
      const item = requisitionItems[index];
      const requiredQuantity = item.requested_quantity - item.fulfilled_quantity;
      setRequisitionItems((prevItems) =>
        prevItems.map((prevItem, i) =>
          i === index
            ? {
                ...prevItem,
                approve_quantity:
                  value > requiredQuantity ? requiredQuantity : value,
                stocks: (value <= 0 || isNaN(value)) ? [] : [EmptyStocks],
              }
            : prevItem
        )
      );
    };

    const handleRequisitionItemStockChange = (index, stockIndex, stockData) => {
      setRequisitionItems((prevItems) =>
        prevItems.map((item, i) => {
          if (i !== index) return item;
          const stocks = item.stocks.map((stock, j) =>
            j === stockIndex ? { ...stock, ...stockData } : stock
          );
          const isExistEmptyStock = stocks.some(
            (stock) => stock.isValid === false
          );

          const approvedQuantity = item.approve_quantity || 0;
          const currentQuantity = stocks.reduce(
            (acc, stock) => acc + parseInt(stock.quantity || 0),
            0
          );
          let remainRequiredQuantity = approvedQuantity - currentQuantity;
          const isFulfilled = remainRequiredQuantity === 0;
          let newStocks = stocks;
          if (!isFulfilled && !isExistEmptyStock) {
            newStocks = [...stocks, EmptyStocks];
          }
          if (isFulfilled && isExistEmptyStock) {
            newStocks = newStocks.filter((stock) => stock.isValid === true);
          }
          return { ...item, stocks: newStocks };
        })
      );
    };

    const handleBarcodeChange = (value, index, stockIndex) => {
      const stockData = {
        barcode_no: value,
        stock_id: "",
        net_quantity: 0,
        quantity: 0,
        rack: "",
        isValid: false,
      };
      handleRequisitionItemStockChange(index, stockIndex, stockData);
    };

    const handleQuantityChange = (value, index, stockIndex) => {
      const elem = document.getElementById(
        `select_quantity-${index}-${stockIndex}`
      );
      const item = requisitionItems[index];
      const currentQuantity = item.stocks.reduce(
        (acc, stock) => acc + parseInt(stock.quantity || 0),
        0
      );
      const approvedQuantity = item.approve_quantity || 0;
      const remainRequiredQuantity = approvedQuantity - currentQuantity;
      const netbarcodeQuantity = item.stocks[stockIndex]?.net_quantity || 0;
      const min = 1; // Set your minimum value here
      let max = netbarcodeQuantity;
      if (netbarcodeQuantity > remainRequiredQuantity) {
        const currentStockQuantity = item.stocks[stockIndex]?.quantity || 0;
        max = approvedQuantity - (currentQuantity - currentStockQuantity);
      }
      value = parseInt(value, 10);
      // Check if the input is within the specified range
      if (value === "" || (value >= min && value <= max)) {
        handleRequisitionItemStockChange(index, stockIndex, {
          quantity: value,
        });
        elem?.classList.remove("is-invalid");
      }
    };

    const hasDuplicatesBarcode = () => {
      const seen = new Set();
      for (const item of requisitionItems) {
        for (const stock of item.stocks) {
          if (stock.barcode_no && seen.has(stock.barcode_no)) {
            return true;
          }
          seen.add(stock.barcode_no);
        }
      }
      return false;
    };

    const getBarcodeStock = async (e, index, stockIndex) => {
      if (e.key === "Enter" && e.target.value.length > 0) {
        if (hasDuplicatesBarcode()) {
          toast.error("Barcode already exists in stock OUT list");
        } else {
          try {
            const item = requisitionItems[index];
            const queryParams = {
              barcode: e.target.value,
              //older_stock_id: requisitionItems.filter((_, ind) => index !== ind).map((item) => item.stock_id),
            };
            const response = await getStockByBarcode(queryParams);
            const { data } = response.data;
            if (item && item.product.name !== data.product) {
              toast.error(
                "Barcode does not match with the product in requisition"
              );
              return;
            }
            const approvedQuantity = item.approve_quantity || 0;
            const currentQuantity = item.stocks.reduce(
              (acc, stock) => acc + parseInt(stock.quantity || 0),
              0
            );
            const remainRequiredQuantity = approvedQuantity - currentQuantity;
            const netQuantity = data.quantity || 0;
            let quantity = 1;
            if (item.product.is_multi_unit) {
              if (netQuantity < remainRequiredQuantity) {
                quantity = netQuantity;
              } else {
                quantity = remainRequiredQuantity;
              }
            }
            const stockData = {
              isValid: true,
              stock_id: data.id,
              quantity,
              net_quantity: netQuantity,
              rack: data.rack,
            };
            handleRequisitionItemStockChange(
              index,
              stockIndex,
              stockData,
              true
            );

            if (data?.oldest_stock) {
              setOldestStock({
                isModalOpen: true,
                stock: data?.oldest_stock,
                index: index,
              });
            }
            document
              .getElementById(`barcode_no-${index}-${stockIndex}`)
              ?.classList.remove("is-invalid");
          } catch (error) {
            toast.error(error?.response?.data?.message);
            document
              .getElementById(`barcode_no-${index}-${stockIndex}`)
              ?.classList.add("is-invalid");
          }
        }
      }
    };

    const handleContinueWithOldStock = () => {
      const index = oldestStock?.index;
      const stockIndex = oldestStock?.stockIndex;
      const data = oldestStock?.stock;
      const stcokData = {
        stock_id: data.id,
        select_quantity: data.quantity,
        net_quantity: data.quantity,
        barcode_no: data.barcode,
      };
      handleRequisitionItemStockChange(index, stockIndex, stcokData);
      toggleOldestStockModal();
    };

    const validateRequisitionItems = () => {
      for (const item of requisitionItems) {
        for (const stock of item.stocks) {
          if (!stock.isValid) {
            toast.error("Please fill all required fields");
            return false;
          }
        }
      }
      return true;
    };

    const handleStockOut = async () => {
      console.log("requisitionItems submited");
      if (!validateRequisitionItems()) {
        return;
      }
      setIsSubmitLoading(true);
      try {
        const itemList = [];
        requisitionItems.forEach((item) => {
          const stockList = [];
          item.stocks.forEach((stock) => {
            if (stock.isValid && stock.quantity > 0) {
              stockList.push({
                stock_id: stock.stock_id,
                quantity: stock.quantity,
                barcode_no: stock.barcode_no,
              });
            }
          });
          if (stockList.length > 0) {
            itemList.push({
              approve_quantity: item.approve_quantity,
              requisition_item_id: item.id,
              stock_list: stockList,
            });
          }
        });
        const payload = { requisition_items: itemList };
        const requisitionId = requisitionNo?.value;
        await stockOutRequisitionItem(requisitionId, payload);
        toast.success("Stock out successful");
        setIsSubmitLoading(false);
        toggleModal();
      } catch (error) {
        toast.error("Stock out failed");
      } finally {
        setIsSubmitLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      submit: handleStockOut,
    }));

    return (
      <>
        <div>
          <div className="card">
            <div className="d-flex flex-inline align-items-center justify-content-between px-3  py-2">
              <div className="d-flex align-items-center justify-content-start gap-2">
                <Select
                  isClearable={false}
                  value={requisitionNo}
                  isDisabled={isLoading}
                  className="react-select"
                  classNamePrefix="select"
                  placeholder="Select Requisition"
                  options={requisitionOption}
                  isLoading={isRequisitionNoLoading}
                  onChange={(data) => {
                    handleRequisitionNoChange(data);
                  }}
                  components={{ LoadingIndicator }}
                />
              </div>
              {requisitionData && (
                <div className="d-flex align-items-center justify-content-between gap-5 flex-wrap">
                  <div className="d-flex flex-column align-items-start">
                    <span>Requested By</span>
                    <strong>{requisitionData.requested_by || "N/A"}</strong>
                  </div>

                  <div className="d-flex flex-column align-items-start">
                    <span>Fulfillment Date</span>
                    <strong>{requisitionData.fulfillment_date || "N/A"}</strong>
                  </div>

                  <div className="d-flex flex-column align-items-start">
                    <span>Status</span>
                    <RequisitionStatusBadge status={requisitionData.status} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {requisitionItems.map((item, index) => (
            <div className="card px-3  py-2 mb-3" key={index}>
              <div className="d-flex flex-wrap align-items-start gap-3">
                <div
                  style={{ backgroundColor: "#fdf6f0" }}
                  className="d-flex justify-content-start gap-4 align-items-center mb-2  p-2 rounded"
                >
                  <div className="d-flex flex-column align-items-start">
                    <span className="text-muted">Product:</span>
                    <span className="fw-bold">{item.product.name}</span>
                  </div>

                  <div className="d-flex flex-column align-items-start">
                    <span className="text-muted">Purpose:</span>
                    <span className="fw-bold">{item.purpose}</span>
                  </div>

                  <div className="d-flex flex-column align-items-start">
                    <span className="text-muted">Requested:</span>
                    <span className="badge badge-sm light badge-secondary">
                      {item.requested_quantity - item.fulfilled_quantity}{" "}
                      Quantity
                    </span>
                  </div>
                  <div className="d-flex flex-column align-items-start">
                    <span className="text-muted">Approve Qnt:</span>
                    <input
                      type="number"
                      min={0}
                      max={item.requested_quantity - item.fulfilled_quantity}
                      value={item.approve_quantity}
                      onChange={(e) => handleApproveQuantityChange(e, index)}
                      className="rounded border px-2 py-1"
                      style={{ width: "80px" }}
                    />
                  </div>
                </div>

                <div className="d-flex align-items-center flex-wrap gap-2">
                  {item.stocks.map((stock, stockIndex) => (
                    <div
                      style={{ backgroundColor: "#ededed" }}
                      key={`${index}-${stockIndex}`}
                      className="d-flex align-items-start flex-wrap gap-3 p-2 rounded"
                    >
                      <div className="d-flex flex-colummn gap-2 align-items-center">
                        <span>Barcode</span>
                        <input
                          type="text"
                          id={`barcode_no-${index}-${stockIndex}`}
                          style={{ width: "10rem" }}
                          autoFocus={stock.barcode_no.length === 0}
                          value={stock.barcode_no}
                          onChange={(e) => {
                            handleBarcodeChange(
                              e.target.value,
                              index,
                              stockIndex
                            );
                          }}
                          onKeyPress={(e) => {
                            getBarcodeStock(e, index, stockIndex);
                          }}
                          className="form-control"
                          placeholder="Barcode"
                        />
                      </div>
                      {stock.isValid && (
                        <>
                          {item.product.is_multi_unit ? (
                            <>
                              <div className="d-flex gap-2 align-items-center">
                                <span>Quantity</span>
                                <div
                                  style={{
                                    minWidth: "8rem",
                                    maxWidth: "10rem",
                                  }}
                                >
                                  <input
                                    type="number"
                                    min={1}
                                    autoFocus={true}
                                    onChange={(e) => {
                                      handleQuantityChange(
                                        e.target.value,
                                        index,
                                        stockIndex
                                      );
                                    }}
                                    value={stock.quantity}
                                    className="form-control"
                                    id={`select_quantity-${index}-${stockIndex}`}
                                    placeholder="Quantity"
                                  />
                                </div>
                              </div>
                              <div className="d-flex flex-column align-itemm-start">
                                <span>
                                  Rack : <strong>{stock.rack || "N/A"}</strong>
                                </span>
                                <span>
                                  Net Stock :{" "}
                                  <strong>{stock.net_quantity || "N/A"}</strong>
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="d-flex flex-column align-itemm-start">
                              <span>
                                Quantity :{" "}
                                <strong>{stock.quantity || "N/A"}</strong>
                              </span>
                              <span>
                                Rack : <strong>{stock.rack || "N/A"}</strong>
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <Modal
          className="modal fade"
          id="exampleModal3"
          show={oldestStock.isModalOpen}
          onHide={toggleOldestStockModal}
          backdrop="static"
          keyboard={false}
        >
          <div className="modal-content bg-warning">
            <div className="modal-body">
              <p className="text-secondary">
                You have chosen a latest stock. There is an older stock
                available on Rack {oldestStock.rack} with barcode{" "}
                <strong>{oldestStock?.stock?.barcode}</strong> and quantity{" "}
                <strong>{oldestStock?.stock?.quantity}</strong>.
                <br />
                Please confirm if you want to proceed with this selection or
                continue with the oldest stock
              </p>
              <div className=" d-flex align-items-center flex-column">
                <button
                  type="submit"
                  className="btn btn-primary mb-2"
                  onClick={handleContinueWithOldStock}
                >
                  Continue with oldest stock
                </button>
                <button
                  type="button"
                  onClick={toggleOldestStockModal}
                  className="btn btn-outline-secondary light ms-1"
                >
                  Proceed with current stock
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
);

export default RequisitionStockOut;
